/**
 * Content of the approved, human-vetted October 2026 Issue-Mapping Memorandum
 * used for the public proof section on the homepage and pricing page.
 *
 * SOURCE OF TRUTH: the PDF at `backend/assets/clarity_sample_memo_oct2026.pdf`
 * (file W3RC-000E3F40, dated 2026-10-08, revised 2026-10-13), served at
 * `/api/sample-memo.pdf` and attached to the Clarity Issue-Mapping Starter Kit.
 * This file mirrors that document for the on-page, scrollable preview so the
 * content is present in prerendered HTML. The memorandum's legal content is
 * never regenerated; keep this excerpt faithful to the PDF.
 */
export const SAMPLE_MEMO_EXCERPT = {
  id: "W3RC-000E3F40",
  fileId: "W3RC-000E3F40",
  memoDate: "October 8, 2026",
  revisionDate: "Revised October 13, 2026",
  dateLabel: "October 8, 2026 · Revised October 13, 2026",
  humanVetted: true,
  scenarioTitle:
    "Offshore token issuer with U.S. users after the stalled market structure bill",
  totalIssues: 8,

  // 1. Intake Consistency Notes
  intakeConsistencyNotes: [
    "Entity status is marked Active, which may not capture the paused staking rewards program and the paused U.S. market entry initiative. Confirm whether those paused activities create continuing obligations.",
    "Parties include the Foundation / SPV and the Custodian / Wallet provider but do not identify the U.S. development company, even though the narrative describes it as a key actor continuing roadmap work. Confirm its legal status and role.",
    "Jurisdictional touchpoints include the European Union, but the narrative describes no EU-specific activity or user targeting. Confirm whether EU users access the platform and whether any entity is established or solicits in the EU.",
    "MiCA's transitional period has ended, so any existing EU-facing activity should be assessed under the currently applicable authorization and conduct framework.",
  ],

  // 2. Plain-Language Brief for boards and non-specialists
  plainLanguageBrief:
    "Your business operates through three connected entities: a foundation in the Cayman Islands that holds the project's token reserves, a development company in the United States that keeps building the technology, and an operating company in the British Virgin Islands that runs the trading platform. U.S. customers have been able to use the platform, which creates a significant risk that U.S. regulators will claim authority over relevant activities and demand that you register, qualify for an exemption, or shut down U.S. access.",

  // 3. Eight prioritized regulatory issues
  issues: [
    {
      domain: "Securities",
      priority: "high",
      headline: "Investment contract status under the March 2026 SEC framework.",
      touchpoints: ["SEC (US)"],
      questions: [
        "Is the token subject to an investment contract today under the March 2026 SEC interpretation, given ongoing U.S. development-company efforts?",
        "Has the token separated from the issuer's promises of essential managerial efforts, or do roadmap work and treasury-funded activities travel with it in secondary trading?",
        "If staking resumes, does it fit the SEC's protocol-staking treatment, or do enhanced or provider-determined rewards require a separate investment-contract analysis?",
      ],
      whyItMatters:
        "Whether transactions in the token remain subject to an investment contract determines whether U.S. securities registration or an exemption is required for continued U.S. access. The August 18, 2026 Regulation Crypto Assets proposal would add a conditional safe harbor, but it is not current law and comments are due October 20, 2026.",
      authorities: ["SEC (US)"],
    },
    {
      domain: "Securities",
      priority: "high",
      headline: "Exchange and intermediary registration given U.S. user participation.",
      touchpoints: ["SEC (US)"],
      questions: [
        "Does the BVI operating company's venue constitute a national securities exchange, alternative trading system, or broker-dealer under U.S. law?",
        "Could failure to register or claim an exemption expose the foundation, the U.S. development company, or affiliates to secondary liability?",
        "If the token has separated from the investment contract, does the venue remain subject to SEC antifraud jurisdiction?",
      ],
      whyItMatters:
        "Operating a platform that facilitates domestic transactions in securities or investment contracts can trigger Exchange Act registration. The analysis turns on whether the relevant transactions are domestic under Morrison, not merely whether offshore activity has U.S. effects.",
      authorities: ["SEC (US)"],
    },
    {
      domain: "AML / KYC / Sanctions",
      priority: "high",
      headline: "Money services business and money transmission obligations across the structure.",
      touchpoints: ["FinCEN (US)", "FATF (International)"],
      questions: [
        "Does the BVI operating company engage in money transmission under FinCEN's 2019 CVC guidance, requiring MSB registration?",
        "Does the custodian or wallet provider require separate FinCEN registration and an AML program under 31 CFR 1022.210?",
        "Where transactions cross borders, does the platform transmit originator and beneficiary information consistent with the FATF Travel Rule?",
      ],
      whyItMatters:
        "Operating a covered CVC exchange without required FinCEN registration can bring civil penalties under 31 U.S.C. 5330, and separate criminal exposure under 18 U.S.C. 1960. An offshore structure does not exempt activity where the U.S. statutory nexus is satisfied.",
      authorities: ["FinCEN (US)", "FATF (International)"],
    },
    {
      domain: "AML / KYC / Sanctions",
      priority: "high",
      headline: "Customer identification, sanctions screening, and AML program sufficiency.",
      touchpoints: ["FinCEN (US)", "OFAC (US)", "BVI FSC (BVI)"],
      questions: [
        "Does the venue perform customer identification consistent with BVI law and the reach of U.S. sanctions authorities where U.S. persons are involved?",
        "Does the platform screen users and transactions against OFAC lists and keep records sufficient to demonstrate compliance?",
        "Does the AML program designate a compliance officer, provide training, and include independent review?",
      ],
      whyItMatters:
        "An inadequate AML program or sanctions screening can bring enforcement by FinCEN, OFAC, or the BVI Financial Services Commission, with penalties scaled to conduct and volume.",
      authorities: ["FinCEN (US)", "OFAC (US)", "BVI FSC (BVI)"],
    },
    {
      domain: "Market Integrity",
      priority: "medium",
      headline: "Market manipulation and trading practices given a multi-jurisdiction user base.",
      touchpoints: ["SEC (US)", "CFTC (US)", "BVI FSC (BVI)", "ESMA (EU)"],
      questions: [
        "Does the venue maintain surveillance adequate to detect manipulation, wash trading, or other prohibited practices across applicable jurisdictions?",
        "Where the foundation holds significant reserves, does potential influence over supply create insider-trading or market-manipulation exposure?",
        "Do order-matching or affiliate trading activities create conflicts of interest or front-running exposure requiring disclosure?",
      ],
      whyItMatters:
        "Manipulation and fraud prohibitions can apply even where the asset is not a security in every jurisdiction. With MiCA's transitional period ended, the current EU market-conduct framework is directly relevant to any EU-facing operations.",
      authorities: ["SEC (US)", "CFTC (US)", "BVI FSC (BVI)", "ESMA (EU)"],
    },
    {
      domain: "Tax",
      priority: "high",
      headline: "Staking rewards characterization and withholding obligations.",
      touchpoints: ["IRS (US)"],
      questions: [
        "If staking resumes, how would the IRS characterize rewards, and does Revenue Ruling 2023-14 determine when they are gross income?",
        "For U.S. recipients, do information-reporting (IRC 6041) and backup-withholding (IRC 3406) obligations apply?",
        "Where rewards are paid to non-U.S. persons, do Chapter 3 withholding provisions apply, and is W-8 documentation or treaty relief needed?",
      ],
      whyItMatters:
        "Revenue Ruling 2023-14 provides that staking rewards are generally gross income when the taxpayer obtains dominion and control. Failure to meet reporting or withholding obligations can bring penalties to the payor and separate exposure for recipients.",
      authorities: ["IRS (US)"],
    },
    {
      domain: "Custody / Safekeeping",
      priority: "medium",
      headline: "Custody obligations for the foundation treasury and user-held assets.",
      touchpoints: ["CIMA (Cayman Islands)", "BVI FSC (BVI)", "SEC (US)"],
      questions: [
        "Does the foundation act as a custodian under Cayman law, and does it meet the Virtual Asset (Service Providers) Act safeguarding obligations?",
        "Do user-asset arrangements meet BVI custody and segregation requirements under SIBA 2010 and the VASP Act 2022?",
        "Does the arrangement satisfy SEC custody rules if U.S. securities characterization applies, and does it qualify for an exemption?",
      ],
      whyItMatters:
        "Custody failures can bring loss of user assets, sanctions, and civil liability. Multi-jurisdiction access may require satisfying the Cayman, BVI, U.S., and EU custody frameworks simultaneously, or demonstrating that particular frameworks do not apply.",
      authorities: ["CIMA (Cayman Islands)", "BVI FSC (BVI)", "SEC (US)"],
    },
    {
      domain: "Corporate / Governance",
      priority: "medium",
      headline: "Affiliate-transaction scrutiny and common control across the three entities.",
      touchpoints: ["SEC (US)", "CIMA (Cayman Islands)", "BVI FSC (BVI)"],
      questions: [
        "Do the foundation, the U.S. development company, and the BVI operating company constitute affiliates under common control, and how does that affect characterization?",
        "Does the foundation's governance and its relationship with the development company create fiduciary duties or disclosure obligations to token holders?",
        "Do foundation-funded roadmap payments constitute self-dealing or related-party transactions requiring board approval or independent review?",
      ],
      whyItMatters:
        "Where a treasury, a developer, and a venue are under common control, regulators and courts may examine the substance of the integrated arrangement rather than treating each entity in isolation.",
      authorities: ["SEC (US)", "CIMA (Cayman Islands)", "BVI FSC (BVI)"],
    },
  ],

  // 4. Domains Not Surfaced and their explanations
  domainsNotSurfaced: [
    { domain: "Consumer Protection", explanation: "No retail-facing marketing claims, product disclosures, or complaint mechanisms were described that would generate standalone issues beyond Securities and Market Integrity." },
    { domain: "Data & Privacy", explanation: "The scenario does not describe personal-data processing in enough detail to generate privacy-specific questions, though obligations arise if the platform collects KYC data." },
    { domain: "Cybersecurity", explanation: "No facts on controls, incident response, or breaches were provided; critical operationally, but not a discrete regulatory issue without more facts." },
    { domain: "Commodities / Derivatives", explanation: "The token is described as a native protocol token, with no indication it is a futures contract, swap, or derivative triggering standalone CFTC obligations beyond antifraud authority." },
    { domain: "Banking & Payments", explanation: "No fiat on-ramps, off-ramps, or banking relationships were described beyond the MSB issues already covered under AML / KYC / Sanctions." },
    { domain: "IP / Smart Contract", explanation: "No facts on smart-contract code, open-source licensing, IP ownership, or audit history were provided to generate standalone issues." },
  ],

  // 5. Selected Regulatory and Legal Sources (a faithful selection; the full
  // list appears in the memorandum)
  selectedSources: [
    "Application of the Federal Securities Laws to Certain Crypto Assets, Release Nos. 33-11412; 34-105020 (March 17, 2026)",
    "Regulation Crypto Assets, Release Nos. 33-11434; 34-106150 (proposed Aug. 18, 2026; comments due Oct. 20, 2026)",
    "SEC v. W.J. Howey Co., 328 U.S. 293 (1946)",
    "Morrison v. National Australia Bank Ltd., 561 U.S. 247 (2010)",
    "SEC v. Telegram Group Inc., No. 19-cv-09439 (S.D.N.Y.)",
    "Digital Asset Market Clarity Act of 2025, H.R. 3633 (Senate cloture not invoked, Sept. 15, 2026)",
    "FIN-2019-G001, FinCEN convertible-virtual-currency guidance (May 9, 2019)",
    "31 U.S.C. 5330; 31 CFR 1022.210, 1022.380 (MSB registration and AML programs)",
    "18 U.S.C. 1960 (unlicensed money transmitting businesses)",
    "FATF Recommendation 16 (Travel Rule)",
    "Revenue Ruling 2023-14; IRC Sections 6041, 3406, and 1441-1446",
    "Virtual Asset (Service Providers) Act, 2020 (Cayman Islands, as amended)",
    "Virtual Assets Service Providers Act, 2022; Securities and Investment Business Act, 2010 (BVI)",
    "Regulation (EU) 2023/1114 (MiCA); Regulation (EU) 2023/1113 (transfers of funds)",
  ],

  // Legacy fields kept so existing call sites render without change.
  summary:
    "A fictional offshore token issuer with three affiliated entities (a Cayman foundation, a U.S. development company, and a BVI operating company) has allowed U.S. users onto its platform. The memorandum organizes the regulatory questions worth investigating across securities, AML, tax, custody, market integrity, and governance, prioritized by exposure.",
  remainingDomains: ["Consumer Protection", "Data & Privacy", "Cybersecurity", "Commodities / Derivatives", "Banking & Payments", "IP / Smart Contract"],
  diligenceQuestion:
    "Do the foundation, the U.S. development company, and the BVI operating company constitute affiliates under common control, and how does that affect characterization?",
};
